/**
 * spoken.ts —— 把「AI 想说的话」从 IL 文本里取出来
 *
 * 背景（这是 M5 修掉的一个真实缺陷）：
 *
 *   失败二轮 / 回答轮里，我们让模型输出 `explain('……')`，
 *   并承诺「这一轮的产出不执行，只当文字展示」。
 *   但最初的实现是——把清洗后的整段文本**原样**丢给 UI。
 *   结果是用户在模态框里看到：
 *
 *       explain('你的通讯录目前没有任何联系人。')
 *
 *   这既难看，又泄露了 IL 这个内部机制。
 *
 * 正确做法：既然这一轮**只可能是 explain**（提示词如此要求），
 * 就把它的字符串实参取出来展示；取不到才退回原文。
 *
 * ⚠️ 关键约束：本函数**绝不执行任何东西**。它只是做字符串切分。
 *    就算模型故意写了 `session.send('...')`，这里也只会把它当普通
 *    文本看一眼——不解析成 AST，不调求值器。
 *
 * 之所以可以「宽松地」用正则而不是走完整 Lexer：
 *   · 这里的输入是模型给的自由文本，不是待执行的 IL；
 *   · 它不需要保证正确性，只需要「尽力好看」；
 *   · 走 Lexer 反而危险——那意味着我们开始把这一轮当成可执行代码看待了。
 */

/** 匹配整段（可含多条 explain）的 explain 调用 */
const CALL_RE = /(?:^|;)\s*explain\s*(?:\.\s*[A-Za-z_$][\w$]*\s*)?\(\s*([\s\S]*?)\s*\)\s*(?=;|$)/g;

/**
 * 先剥掉 Markdown 代码围栏。
 *
 * 正常链路上 sanitize() 已经做过了，但本模块是纯函数、可能被单独调用
 * （比如调试页直接贴一段模型输出），所以自带一层兜底。
 */
function stripFence(text: string): string {
  const m = text.match(/(?:```|~~~)[^\n]*\n([\s\S]*?)\n?\s*(?:```|~~~)/);
  return m ? m[1] : text;
}

/**
 * 从一段（可能是 explain 调用的）文本里提取要展示给用户的话。
 *
 * 返回规则：
 *   · 全是 explain 调用 → 各条文本用换行拼起来
 *   · 完全不是 explain   → 原样返回（保持旧行为，不做语义猜测）
 *   · explain 无参数     → 忽略该条
 *
 * @param code 清洗后的文本（第 2 轮的产出）
 */
export function extractSpoken(code: string): string {
  const text = stripFence(code ?? '').trim();
  if (!text) return '';

  // 先快速判断：没有 explain 就完全不动它。
  // 为什么不用「不是 explain 就报错」：提示词要求了 explain，但模型
  // 偶尔会漏。这时原样展示至少不会丢信息，而报错会让用户看到空白。
  if (!/explain\s*(?:\.\s*[A-Za-z_$][\w$]*\s*)?\(/.test(text)) {
    return text;
  }

  const parts: string[] = [];
  let matchedAny = false;

  for (const m of text.matchAll(CALL_RE)) {
    matchedAny = true;
    const raw = m[1];
    const spoken = readStringLiteral(raw);
    if (spoken !== null) parts.push(spoken);
  }

  // 一条 explain 都没匹配上（可能是畸形写法），退回原文
  if (!matchedAny || parts.length === 0) return text;

  return parts.join('\n').trim();
}

/**
 * 把 `'…'` / `"…"` 形式的字面量还原成字符串；不是字面量就返回 null。
 *
 * 只处理最外层的引号 + 反斜杠转义（\n \t \' \" \\）。
 * 不做 Unicode 转义还原 —— 模型几乎不会输出 \uXXXX，
 * 为了这点覆盖率引入风险不划算。
 */
function readStringLiteral(raw: string): string | null {
  const s = (raw ?? '').trim();
  if (s.length < 2) return null;

  const quote = s[0];
  if (quote !== "'" && quote !== '"') {
    // 不是字符串字面量（可能是数字、标识符，或残缺）。不猜。
    return null;
  }
  // 必须成对闭合
  if (s[s.length - 1] !== quote) return null;

  const body = s.slice(1, -1);
  let out = '';
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (ch !== '\\') {
      out += ch;
      continue;
    }
    // 转义序列
    const next = body[i + 1];
    i++;
    switch (next) {
      case 'n':
        out += '\n';
        break;
      case 't':
        out += '\t';
        break;
      case 'r':
        out += '\r';
        break;
      case '\\':
        out += '\\';
        break;
      case "'":
        out += "'";
        break;
      case '"':
        out += '"';
        break;
      default:
        // 未知转义：原样保留反斜杠和字符，别静默吞掉内容
        out += next ?? '';
        break;
    }
  }
  return out;
}

/**
 * 供调试/测试：判断一段文本是不是「纯 explain」（每条语句都是 explain）。
 *
 * 不用「一个正则吃掉全文」——那样 `[\s\S]*?` 会跨语句匹配，
 * 把 `explain('a'); setting.setTheme('dark')` 也误判成纯 explain。
 * 正确做法是逐条语句判断。
 */
export function isExplainOnly(code: string): boolean {
  const text = stripFence(code ?? '').trim();
  if (!text) return false;

  // 按 ; 切分，但只切引号外的（避免字符串里的分号被误切）
  const statements = splitStatements(text);
  if (statements.length === 0) return false;

  return statements.every((s) => /^explain\s*(?:\.\s*[A-Za-z_$][\w$]*\s*)?\(/.test(s.trim()));
}

/**
 * 按 `;` 切分语句，跳过字符串字面量内部的分号。
 * 与 Lexer 的规则一致，但这里只需要切分，不需要理解内容。
 */
function splitStatements(text: string): string[] {
  const out: string[] = [];
  let buf = '';
  let quote: string | null = null;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (quote) {
      buf += ch;
      if (ch === '\\') {
        // 转义：把下一个字符也吞进缓冲
        buf += text[i + 1] ?? '';
        i++;
        continue;
      }
      if (ch === quote) quote = null;
      continue;
    }

    if (ch === "'" || ch === '"') {
      quote = ch;
      buf += ch;
      continue;
    }

    if (ch === ';') {
      if (buf.trim()) out.push(buf);
      buf = '';
      continue;
    }

    buf += ch;
  }

  if (buf.trim()) out.push(buf);
  return out;
}
